import { observer } from "mobx-react";
import { ITableColumn, TableModel, TableRow } from "../../table/table.model";
import { Table } from "../../table/table";
import { useStore } from "app/context";
import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useHistory } from "react-router";
import { AnalyticsStoreDetail } from "./AnalyticsStoreDetail";

interface AnalyticsStoreRow {
    dataType: string;
    analyticsStore: string;
    dbTable: string;
    supportsTimeRange: boolean;
    aggStrategy: string;
    aggregateMethodCount: number;
    nonAggregateMethodCount: number;
    aggregatableFieldCount: number;
    nonAggregatableFieldCount: number;
    dimensionCount: number;
    // full lists for tooltips
    aggregateMethods: string;
    nonAggregateMethods: string;
    aggregatableFields: string;
    nonAggregatableFields: string;
    dimensions: string;
}

interface AnalyticsStoresListProps {
    searchFilter: string;
}

export const AnalyticsStoresList = observer(({ searchFilter }: AnalyticsStoresListProps) => {
    const { adminDatenStandStore: datenStandStore } = useStore();
    const location = useLocation();
    const history = useHistory();

    const selectedDataType = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get('detailDataType');
    }, [location.search]);

    const tm = useMemo(() => {
        const tm = new TableModel<AnalyticsStoreRow, string>();
        const cols: ITableColumn<AnalyticsStoreRow, string>[] = [
            {
                label: 'DataType',
                path: 'dataType',
            },
            {
                label: 'Analytics Store',
                path: 'analyticsStore',
            },
            // {
            //     label: 'DB Table',
            //     path: 'dbTable',
            // },
            {
                label: 'Time Range',
                path: 'supportsTimeRange',
                render: (row) => <>{row.data.supportsTimeRange ? '✓' : '—'}</>,
            },
            {
                label: 'Agg Strategy',
                path: 'aggStrategy',
            },
            {
                label: 'Agg Methods',
                path: 'aggregateMethods',
                render: (row) => (
                    <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '11px', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        {row.data.aggregateMethods.split('\n').filter(Boolean).map((m, i) => <li key={i}>{m}</li>)}
                    </ul>
                ),
            },
            {
                label: 'Non-Agg Methods',
                path: 'nonAggregateMethods',
                render: (row) => (
                    <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '11px', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        {row.data.nonAggregateMethods.split('\n').filter(Boolean).map((m, i) => <li key={i}>{m}</li>)}
                    </ul>
                ),
            },
            {
                label: 'Agg Fields',
                path: 'aggregatableFieldCount',
                render: (row) => (
                    <span title={row.data.aggregatableFields}>
                        {row.data.aggregatableFieldCount}
                    </span>
                ),
            },
            {
                label: 'Non-Agg Fields',
                path: 'nonAggregatableFieldCount',
                render: (row) => (
                    <span title={row.data.nonAggregatableFields}>
                        {row.data.nonAggregatableFieldCount}
                    </span>
                ),
            },
            {
                label: 'Dimensions',
                path: 'dimensionCount',
                render: (row) => (
                    <span title={row.data.dimensions}>
                        {row.data.dimensionCount}
                    </span>
                ),
            },
        ];

        tm.setCols(cols);
        tm.sortBy = 'dataType';
        tm.sortAsc = true;
        tm.idProperty = 'dataType';
        tm.idType = 'string';

        tm.onRowClick = (row: TableRow<AnalyticsStoreRow>) => {
            const params = new URLSearchParams(history.location.search);
            params.set('detailDataType', row.data.dataType);
            history.replace(history.location.pathname + '?' + params.toString());
        };

        return tm;
    }, []);

    useEffect(() => {
        datenStandStore.findAll().then(res => {
            let rows: AnalyticsStoreRow[] = res
                .filter(item => !!item.analyticsStore)
                .map(item => ({
                    dataType: item.dataType,
                    analyticsStore: item.analyticsStore || '',
                    dbTable: item.dbTable || '',
                    supportsTimeRange: !!item.supportsTimeRange,
                    aggStrategy: item.aggStrategy || '',
                    aggregateMethods: (item.aggregateMethods || []).join('\n'),
                    nonAggregateMethods: (item.nonAggregateMethods || []).join('\n'),
                    aggregatableFields: (item.aggregatableFields || []).join('\n'),
                    nonAggregatableFields: (item.nonAggregatableFields || []).join('\n'),
                    dimensions: (item.dimensions || []).join('\n'),
                    aggregateMethodCount: (item.aggregateMethods || []).length,
                    nonAggregateMethodCount: (item.nonAggregateMethods || []).length,
                    aggregatableFieldCount: (item.aggregatableFields || []).length,
                    nonAggregatableFieldCount: (item.nonAggregatableFields || []).length,
                    dimensionCount: (item.dimensions || []).length,
                }));

            if (searchFilter) {
                const filter = searchFilter.toLowerCase();
                rows = rows.filter(r =>
                    r.dataType.toLowerCase().includes(filter) ||
                    r.analyticsStore.toLowerCase().includes(filter) ||
                    r.dbTable.toLowerCase().includes(filter) ||
                    r.aggStrategy.toLowerCase().includes(filter)
                );
            }

            tm.setRowData(rows);
        });
    }, [datenStandStore, searchFilter]);

    const handleCloseDetail = useCallback(() => {
        const params = new URLSearchParams(history.location.search);
        params.delete('detailDataType');
        const newSearch = params.toString();
        history.replace(history.location.pathname + (newSearch ? '?' + newSearch : ''));
    }, [history]);

    return (
        <>
            <div className="table-container">
                <Table stickyHeader={true} tm={tm} />
            </div>
            {selectedDataType && <AnalyticsStoreDetail dataType={selectedDataType} onClose={handleCloseDetail} />}
        </>
    );
});
